import type { SalaryBand } from '../../services/salaryBandsApi';

export interface SalaryBandRow {
  band: SalaryBand;
  highlighted: boolean;
}

/** One role's ladder: every band of one role code in one department, lowest level first. */
export interface SalaryBandLadder {
  key: string;
  /** What the ladder is called: the title at `NAMING_LEVEL`, e.g. "Financial Controller". */
  role: string;
  department: string;
  careerPath: string;
  rows: SalaryBandRow[];
  minLevel: number;
  maxLevel: number;
  hasHighlight: boolean;
}

/**
 * The department segment of a band identifier — a mirror of `DEPARTMENT_PREFIXES` in
 * `proxy-server/lib/salaryBandMatch.js`, which owns the identifier format
 * (`DE` + department prefix + role code + level, e.g. `DEFIAC3`). A test pins the two lists
 * together. Two-letter prefixes are tried before the legacy one-letter `A`.
 */
export const DEPARTMENT_PREFIXES = ['A', 'EN', 'FI', 'FL', 'GR', 'OP', 'PL'];
const PREFIXES_LONGEST_FIRST = [...DEPARTMENT_PREFIXES].sort((a, b) => b.length - a.length);

/**
 * The role code inside a band identifier (`DEFIAC3` → `AC`), or null when the identifier does
 * not have the expected shape. Null is not a guess: those bands are grouped by career path
 * instead, so an odd identifier lands in a sensible ladder rather than in a wrong one.
 */
export function roleCodeOf(identifier: string): string | null {
  const match = /^DE([A-Z]+?)(\d+)$/.exec(`${identifier ?? ''}`.trim().toUpperCase());
  if (!match) return null;
  const head = match[1];
  const prefix = PREFIXES_LONGEST_FIRST.find((candidate) => head.startsWith(candidate) && head.length > candidate.length);
  return prefix ? head.slice(prefix.length) : null;
}

const ladderKey = (band: SalaryBand) => {
  const code = roleCodeOf(band.identifier);
  return code ? `${band.department}\u0000role\u0000${code}` : `${band.department}\u0000path\u0000${band.careerPath}`;
};

/**
 * Groups bands into one ladder per ROLE (department + the role code in the identifier) — how
 * the People team thinks about bands: "Financial Controller, L1 to L6", not "Controlling".
 *
 * Not consecutive grouping: the proxy sorts by career path and then level, so two roles sharing
 * a path (B2B Sales Manager and Inside Sales Associate under Sales) arrive interleaved by level.
 * Ladders keep the order in which their first band arrived; rows inside a ladder go by level,
 * stable within one level (so BA stays before MA). Every row keeps its own title.
 */
export function groupBandLadders(bands: SalaryBand[], highlightIdentifiers: string[] = []): SalaryBandLadder[] {
  const highlight = new Set(highlightIdentifiers.filter(Boolean));
  const byKey = new Map<string, SalaryBandLadder>();
  for (const band of bands) {
    const key = ladderKey(band);
    let ladder = byKey.get(key);
    if (!ladder) {
      ladder = {
        key,
        role: '',
        department: band.department,
        careerPath: band.careerPath,
        rows: [],
        minLevel: band.level,
        maxLevel: band.level,
        hasHighlight: false,
      };
      byKey.set(key, ladder);
    }
    const highlighted = highlight.has(band.identifier);
    ladder.rows.push({ band, highlighted });
    ladder.minLevel = Math.min(ladder.minLevel, band.level);
    ladder.maxLevel = Math.max(ladder.maxLevel, band.level);
    ladder.hasHighlight ||= highlighted;
  }
  const ladders = [...byKey.values()];
  for (const ladder of ladders) {
    ladder.rows.sort((a, b) => a.band.level - b.band.level);
    ladder.role = ladderName(ladder);
  }
  return ladders;
}

/**
 * The level a ladder is named after. Every role starts at an associate level, so the lowest
 * title reads "Associate …" and names the entry rung rather than the role; level 2 carries the
 * role's own title (David, Oct 2 2026). A ladder with no level-2 band falls back to its lowest
 * titled level, so it is never left unnamed.
 */
export const NAMING_LEVEL = 2;

function ladderName(ladder: SalaryBandLadder): string {
  const titled = ladder.rows.filter((row) => row.band.title);
  return (
    titled.find((row) => row.band.level === NAMING_LEVEL)?.band.title ||
    titled[0]?.band.title ||
    ladder.careerPath ||
    '—'
  );
}

/** Above this many bands the ladders start collapsed, so the table opens as an overview. */
export const COLLAPSE_ABOVE_BANDS = 30;

/**
 * Whether a ladder is open. A long table starts collapsed; a short one (my band, a colleague's
 * path) starts open. A ladder holding the highlighted band and every ladder while a filter is
 * typed start open, because a filter that matches rows inside closed ladders would look like it
 * matched nothing. `toggled` holds the ladders someone clicked, so a click always flips what is
 * on screen whatever the default was.
 */
export function ladderOpen(
  ladder: SalaryBandLadder,
  { totalBands, filtering, toggled, allOpen }: {
    totalBands: number;
    filtering: boolean;
    toggled: ReadonlySet<string>;
    allOpen: boolean | null;
  },
): boolean {
  const byDefault = allOpen ?? (filtering || ladder.hasHighlight || totalBands <= COLLAPSE_ABOVE_BANDS);
  return toggled.has(ladder.key) ? !byDefault : byDefault;
}
